/**
 * Author: Claude Code
 * Version: v3.2.138
 * Modified: 2025-10-17
 *
 * LoadingScreenTest - Mock test page for frontend loading event flow
 * Tests frontend-only logic by manually triggering WebSocket events
 */
import { useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import webSocketService from '../services/websocket/WebSocketService';

export default function LoadingScreenTest() {
  const [showLoading, setShowLoading] = useState(false);
  const [testLog, setTestLog] = useState<string[]>([]);

  const addLog = (message: string) => {
    setTestLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  const mockLoadingFlow = () => {
    setShowLoading(true);
    addLog('🧪 [TEST] Starting mock loading flow');

    // Mock: loading started
    setTimeout(() => {
      addLog('📡 [MOCK] Triggering game:loading:started');
      webSocketService.triggerGameEvent('game:loading:started', {
        roomId: 'test_room_mock',
        totalStages: 5,
        estimatedDuration: 8000
      });
    }, 100);

    // Mock: progress updates (5 stages)
    const stages = [
      { percentage: 0, message: 'Initializing room dependencies...', stage: 'room_init' },
      { percentage: 20, message: 'Creating game engine...', stage: 'game_init' },
      { percentage: 40, message: 'Registering agents...', stage: 'agent_registration' },
      { percentage: 60, message: 'Generating game map...', stage: 'map_generation' },
      { percentage: 80, message: 'Preloading assets...', stage: 'asset_preload' }
    ];

    stages.forEach((stage, index) => {
      setTimeout(() => {
        addLog(`📊 [MOCK] Progress: ${stage.percentage}% - ${stage.message}`);
        webSocketService.triggerGameEvent('game:loading:progress', {
          roomId: 'test_room_mock',
          stage: stage.stage,
          stageIndex: index + 1,
          totalStages: 5,
          percentage: stage.percentage,
          message: stage.message
        });
      }, (index + 1) * 500);
    });

    // Mock: loading completed
    setTimeout(() => {
      addLog('✅ [MOCK] Triggering game:loading:completed');
      webSocketService.triggerGameEvent('game:loading:completed', {
        roomId: 'test_room_mock',
        totalDuration: 2500,
        readyToStart: true
      });
    }, 3000);
  };

  const handleLoadingComplete = () => {
    addLog('✅ [CALLBACK] LoadingScreen onComplete triggered');
    alert('✅ Loading completed!\n\nIn real app, this would navigate to /game');
    setShowLoading(false);
  };

  if (showLoading) {
    return <LoadingScreen onComplete={handleLoadingComplete} />;
  }

  return (
    <div style={{
      padding: '50px',
      maxWidth: '800px',
      margin: '0 auto',
      fontFamily: 'monospace'
    }}>
      <h1 style={{ marginBottom: '20px' }}>🧪 LoadingScreen Mock Test</h1>

      <div style={{ marginBottom: '30px' }}>
        <p><strong>Purpose:</strong> Test frontend loading event flow without backend</p>
        <p><strong>Tests:</strong></p>
        <ul>
          <li>✅ WebSocketService receives and forwards loading events</li>
          <li>✅ LoadingScreen listens and updates UI</li>
          <li>✅ Progress bar animation (0% → 20% → 40% → 60% → 80% → 100%)</li>
          <li>✅ onComplete callback triggers navigation</li>
        </ul>
      </div>

      <button
        onClick={mockLoadingFlow}
        style={{
          padding: '15px 30px',
          fontSize: '16px',
          fontWeight: 'bold',
          backgroundColor: '#4ade80',
          color: '#000',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '30px'
        }}
      >
        🚀 Start Mock Loading Flow
      </button>

      <div style={{
        backgroundColor: '#1a1a1a',
        color: '#00ff00',
        padding: '20px',
        borderRadius: '8px',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        <h3 style={{ marginTop: 0, color: '#4ade80' }}>Test Log:</h3>
        {testLog.length === 0 ? (
          <p style={{ color: '#666' }}>No logs yet. Click "Start Mock Loading Flow" to begin.</p>
        ) : (
          testLog.map((log, index) => (
            <div key={index} style={{ marginBottom: '5px' }}>
              {log}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
